import React, { useEffect, useState, useMemo } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  createPCTransaction,
  getPCTransactionById,
  updatePCTransaction,
} from '../../../axios/ProjectCostTransactionAxios';
import Swal from 'sweetalert2';
import PCJournalGroups from '../../../components/PCJournalGroups';
import LoadingOverlay from '../../../components/LoadingOverlay';

function ProjectCostTransactionAdd() {
  const { id } = useParams(); // 🔥 1. Ambil ID dari URL
  const isEditMode = !!id; // 🔥 1. Tentukan apakah dalam mode Edit/Update

  const breadcrumbItems = [
    // 🔥 2. Sesuaikan Breadcrumb
    { label: 'Dashboard', href: '/' },
    { label: 'Project Budgeting', href: '/project-cost-transaction' },
    {
      label: isEditMode
        ? 'Update Project Budgeting'
        : 'Create Project Budgeting',
    },
  ];

  const navigation = useNavigate();
  const [formData, setFormData] = useState({
    project: '',
    location: '',
    year: '',
    poNo1: '',
    poNo2: '',
    journals: [],
  });

  const [loading, setLoading] = useState(false);

  // 🔥 3. FUNGSI UNTUK FETCH DATA JIKA DALAM MODE EDIT
  useEffect(() => {
    if (isEditMode) {
      setLoading(true);
      getPCTransactionById(id)
        .then((response) => {
          // Ganti 'data' menjadi 'response' agar lebih jelas
          // Cek apakah data utama tersedia
          const apiData = response.data || {};

          // Pastikan setiap journal memiliki groupId dan adalah array
          const journalsWithGroupId = (apiData.journals || []).map((j) => ({
            ...j,
            groupId: j.groupId || 'A.1',
          }));

          // console.log(journalsWithGroupId); // Gunakan apiData

          setFormData({
            project: apiData.project || '',
            location: apiData.location || '',
            year: apiData.year ? apiData.year.toString() : '',
            poNo1: apiData.poNo1 || '',
            poNo2: apiData.poNo2 || '',
            journals: journalsWithGroupId,
          });
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          Swal.fire(
            'Error',
            'Failed to fetch transaction data: ' + err.message,
            'error'
          );
        });
    }
  }, [id, isEditMode]);

  useEffect(() => {
    const forms = document.querySelectorAll('.needs-validation');
    Array.from(forms).forEach((form) => {
      form.addEventListener(
        'submit',
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            event.stopPropagation();
          }
          form.classList.add('was-validated');
        },
        false
      );
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const formElements = Array.from(e.target.form.elements);
      const index = formElements.indexOf(e.target);
      formElements[index + 1]?.focus();
    }
  };

  // Update journals dari tabel langsung ke formData
  const setJournals = (items) => {
    setFormData((prev) => ({
      ...prev,
      journals: items,
    }));
  };

  // 🔥 4. Sesuaikan handleSubmit untuk CREATE atau UPDATE
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        denyButton: 'btn btn-danger mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });

    try {
      const result = await swalWithBootstrapButtons.fire({
        title: 'Do you want to save the data?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: `Don't save`,
      });

      if (result.isConfirmed) {
        setLoading(true);

        const payload = {
          ...formData,
          total_cost: grandTotal,
        };

        try {
          if (isEditMode) {
            // MODE UPDATE
            await updatePCTransaction(id, payload);

            swalWithBootstrapButtons.fire(
              'Updated!',
              'The transaction has been updated.',
              'success'
            );
          } else {
            // MODE CREATE
            await createPCTransaction(payload);

            swalWithBootstrapButtons.fire(
              'Saved!',
              'The transaction has been created.',
              'success'
            );
          }
          navigation('/project-cost-transaction');
        } catch (err) {
          swalWithBootstrapButtons.fire({
            title: 'Error!',
            text: err.message,
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else if (result.isDenied) {
        swalWithBootstrapButtons.fire('Changes are not saved', '', 'info');
      }
    } catch (error) {
      swalWithBootstrapButtons.fire({
        title: 'Error!',
        text: 'There was an error processing the transaction.',
        icon: 'error',
      });
    }
  };

  const handleCancel = () => {
    // ... (Logika handleCancel tidak berubah, tetap berfungsi baik)
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: 'Are you sure?',
        text: 'All your input will be cleared.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, clear it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.isConfirmed) {
          const form = document.querySelector('.needs-validation');

          // Clear input form
          form.reset();

          // Reset state
          setFormData({
            project: '',
            location: '',
            year: '',
            poNo1: '',
            poNo2: '',
            journals: [],
          });

          // Reset table
          setJournals([]);

          form.classList.remove('was-validated');
        }
      });
  };

  // Ambil tahun saat ini
  const currentYear = new Date().getFullYear();
  const yearRange = 5; // Rentang tahun: 5 tahun ke belakang dan 5 tahun ke depan

  // Fungsi untuk membuat array tahun
  const getYearOptions = () => {
    const years = [];
    // Mulai dari 5 tahun ke belakang
    const startYear = currentYear - yearRange;
    // Sampai 5 tahun ke depan
    const endYear = currentYear + yearRange;

    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  // const grandTotal = formData.journals.reduce(
  //   (sum, row) => sum + (parseInt(row.total) || 0),
  //   0
  // );

  const grandTotal = useMemo(() => {
    return formData.journals.reduce(
      (sum, row) => sum + (parseInt(row.total) || 0),
      0
    );
  }, [formData.journals]);

  // console.log('data sata', formData.journals);

  return (
    <div>
      {/* 🔥 Sesuaikan pesan loading */}
      <LoadingOverlay
        show={loading}
        message={
          isEditMode
            ? 'Loading transaction data...'
            : 'Saving transaction data...'
        }
      />
      <Breadcrumb items={breadcrumbItems}></Breadcrumb>

      <form
        className="needs-validation"
        noValidate
        autocomplete="off"
        onSubmit={handleSubmit}
      >
        {/* === INPUT FORM === */}
        <div className="row">
          <div className="col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <label className="me-3" style={{ width: '100px' }}>
                    Project
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="project"
                    value={formData.project}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label className="me-3" style={{ width: '100px' }}>
                    Location
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label className="me-3" style={{ width: '100px' }}>
                    PO. NO1
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="poNo1"
                    value={formData.poNo1}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-6">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  <label className="me-3" style={{ width: '100px' }}>
                    Year
                  </label>

                  <select
                    className="form-select form-select-sm w-100" // Kelas untuk seragamkan panjang
                    name="year"
                    value={formData.year || ''}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Pilih tahun
                    </option>
                    {getYearOptions().map(
                      (
                        year // Gunakan fungsi getYearOptions()
                      ) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <div className="d-flex align-items-center mb-3">
                  <label className="me-3" style={{ width: '100px' }}>
                    PO. NO2
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    name="poNo2"
                    value={formData.poNo2}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* === TABEL JURNALS === */}
        <div className="col-sm-12">
          <PCJournalGroups
            journals={formData.journals}
            setJournals={(items) =>
              setFormData((prev) => ({ ...prev, journals: items }))
            }
          />
        </div>
        <div className="card mt-3">
          <div className="card-body d-flex justify-content-between fw-bold">
            <span>Grand Total</span>
            <span>Rp {grandTotal.toLocaleString('id-ID')}</span>
          </div>
        </div>

        {/* === BUTTON === */}
        <div className="d-flex justify-content-end mt-3">
          <button type="submit" className="btn btn-primary mx-2">
            <i className="bi bi-send"></i>
            <span> {isEditMode ? 'Update' : 'Submit'}</span>
          </button>

          <button
            type="button"
            className="btn btn-danger"
            onClick={handleCancel}
          >
            <i className="bi bi-x-circle"></i>
            <span> Clear/Cancel</span>
          </button>

          <Link
            type="button"
            className="btn btn-warning mx-2"
            to="/project-cost-transaction"
          >
            <i className="bi bi-arrow-left-circle"></i>
            <span> Back</span>
          </Link>
        </div>
      </form>
    </div>
  );
}

export default ProjectCostTransactionAdd;
